import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { PeriodPicker } from '@/components/ui/period-picker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

// Imports dos novos cards premium
import { LeadCardTotalPorVideo } from './cards/LeadCardTotalPorVideo';
import { LeadCardQualidadeScore } from './cards/LeadCardQualidadeScore';
import { LeadCardVideoTopCaptura } from './cards/LeadCardVideoTopCaptura';
import { ChartConversaoDia } from '../reports/metrics/charts/ChartConversaoDia';
import { ChartConversaoHorario } from '../reports/metrics/charts/ChartConversaoHorario';
import { ChartDispositivos } from '../reports/metrics/charts/ChartDispositivos';
import { ChartOrigem } from '../reports/metrics/charts/ChartOrigem';

interface VSLeadsMetricsContentProps {
  videoId: string;
  captureType: 'email' | 'senha';
}

export function VSLeadsMetricsContent({ videoId, captureType }: VSLeadsMetricsContentProps) {
  useLanguage();
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showEmailCapture, setShowEmailCapture] = useState(true);
  const [showPasswordCapture, setShowPasswordCapture] = useState(true);

  // Mock data - substituir por dados reais da API
  const mockData = {
    porDia: [
      { date: '2025-10-01', leads: 45 },
      { date: '2025-10-02', leads: 52 },
      { date: '2025-10-03', leads: 38 },
      { date: '2025-10-04', leads: 61 },
      { date: '2025-10-05', leads: 55 },
      { date: '2025-10-06', leads: 48 },
      { date: '2025-10-07', leads: 72 }
    ],
    porHorario: Array.from({ length: 7 }, (_, day) =>
      Array.from({ length: 24 }, (_, hour) => ({
        day,
        hour,
        value: Math.floor(Math.random() * 50) + 10
      }))
    ).flat(),
    dispositivos: [
      { tipo: 'mobile' as const, valor: 423, porcentagem: 65 },
      { tipo: 'desktop' as const, valor: 150, porcentagem: 23 },
      { tipo: 'tablet' as const, valor: 78, porcentagem: 12 }
    ],
    origens: [
      { nome: 'Facebook', valor: 145, icone: 'facebook', cor: '#1877F2' },
      { nome: 'Instagram', valor: 89, icone: 'instagram', cor: '#E4405F' },
      { nome: 'Google', valor: 67, icone: 'chrome', cor: '#4285F4' },
      { nome: 'Direct', valor: 43, icone: 'globe', cor: '#10B981' },
      { nome: 'Outros', valor: 27, icone: 'more-horizontal', cor: '#6B7280' }
    ]
  };

  return (
    <div className="space-y-8">
      {/* BARRA DE FILTROS PREMIUM */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 py-5 border-b">
        <PeriodPicker value={dateRange} onChange={setDateRange} className="sm:w-[240px]" />
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Checkbox 
              id="email-capture" 
              checked={showEmailCapture}
              onCheckedChange={(checked) => setShowEmailCapture(checked as boolean)}
            />
            <Label htmlFor="email-capture" className="text-sm cursor-pointer">{translateInterfaceText("Dados")}</Label>
          </div>
          
          <div className="flex items-center gap-3">
            <Checkbox 
              id="password-capture" 
              checked={showPasswordCapture}
              onCheckedChange={(checked) => setShowPasswordCapture(checked as boolean)}
            />
            <Label htmlFor="password-capture" className="text-sm cursor-pointer">{translateInterfaceText("Senha")}</Label>
          </div>
        </div>
        
        <div className="flex gap-2 lg:ml-auto">
          <Button variant="outline">{translateInterfaceText("Limpar")}</Button>
          <Button>{translateInterfaceText("Exportar CSV")}</Button>
        </div>
      </div>

      {/* CARDS KPI PRINCIPAIS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* CARD 1: Total de Leads por Vídeo */}
        <LeadCardTotalPorVideo 
          videoId={videoId}
          emailCount={856}
          passwordCount={378}
          total={1234}
          growth={15}
        />
        
        {/* CARD 2: Qualidade dos Leads (Score Radar) */}
        <LeadCardQualidadeScore 
          className="lg:col-span-2"
          scores={{
            tempoPreenchimento: 85,
            dispositivo: 78,
            conversao: 88,
            completude: 92,
            scoreGeral: 83.6
          }}
        />
      </div>

      {/* CARD 3: Vídeo com Maior Captura */}
      <LeadCardVideoTopCaptura 
        videoName="VSL Lançamento Q4"
        leadCount={342}
        growth={37}
      />

      {/* GRÁFICOS PREMIUM - Grid 2 colunas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Conversão por Dia */}
        <ChartConversaoDia data={mockData.porDia} />
        
        {/* Conversão por Horário */}
        <ChartConversaoHorario key="heatmap-premium-v2" data={mockData.porHorario} />
        
        {/* Dispositivos (Doughnut) */}
        <ChartDispositivos data={mockData.dispositivos} />
        
        {/* Origem do Lead */}
        <ChartOrigem data={mockData.origens} />
      </div>

      {/* BLOCO DE INSIGHTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="rounded-[14px] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{translateInterfaceText("📊 Principais Insights")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Horário campeão: 22h-24h com 238 conversões")}</p>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Dispositivo principal: Mobile (65% das capturas)")}</p>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Taxa de completude de dados: 92% (excelente)")}</p>
          </CardContent>
        </Card>

        <Card className="rounded-[14px] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{translateInterfaceText("💡 Recomendações")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Investir mais em tráfego móvel (maior conversão)")}</p>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Escalar campanhas entre 22h-24h")}</p>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("• Testar captura com senha neste horário")}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
